import { API_TOKEN_COOKIE } from "@/config/constants";
import Cookie from "./cookie";
import { getOrderParams, emptyStringSanitizer, handleOnFail } from "./client";
import { doTriggerDownload } from "../filters/funcs";
import { isProdENV } from "../filters/utils";
const axios = require("axios");
const qsStringify = require("qs/lib/stringify");

const getToken = () =>
  process.env.REACT_APP_API_HARDCODED_TOKEN || Cookie.getItem(API_TOKEN_COOKIE);

const axiosApiInstance = axios.create();

axiosApiInstance.defaults.headers.common["Authorization"] =
  "Bearer " + getToken();
axiosApiInstance.defaults.headers["Content-Type"] = "application/ld+json";

// Request interceptor for TESTING API calls with mock up of response code
/* axiosApiInstance.interceptors.request.use(
  (config) => {
    if (config.url.includes("403") && !config.url.includes("users/me")) {
      const error = new Error("Forced 403");
      error.response = { status: 403, data: {}, config };
      error.config = config;
      return Promise.reject(error);
    }
    return config;
  },
  (error) => Promise.reject(error)
); */

// Response interceptor for API calls
axiosApiInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error) {
    switch (error.response.status) {
      case 401:
        const originalRequest = error.config;
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          const response = await refreshAccessToken();
          if (response.data.emmsUserId) {
            const newToken = "Bearer " + getToken();
            axiosApiInstance.defaults.headers.common["Authorization"] =
              newToken;
            originalRequest.headers.Authorization = newToken;
            return axiosApiInstance(originalRequest);
          } else if (isProdENV()) {
            window.location = "/login/logout.php";
          }
        }
        break;
      case 403:
        if (error.config.url.includes("users/me") && isProdENV()) {
          window.location = "/login/logout.php";
        }
        const method = error.config.method.toUpperCase();
        if (method === "GET") {
          await callApi({
            url: "/logging/frontendApps",
            method: "POST",
            params: {
              level: "error",
              message: `type: API returned 403, method: ${method}, requestUrl: ${error.config.url} location: ${window.location.pathname}`,
              platform: "js"
            }
          });
          window.location = "/error";
        }
        if (error.config?.onFail) {
          handleOnFail(error);
        }
        return Promise.reject(error);
      case 417:
        // this covers errors from account card change / activate
        error.config.onFail({
          generalError: error.response?.data?.["hydra:description"],
          error
        });
        break;
      case 422:
        if (error.config?.onFail) {
          handleOnFail(error);
        }
        break;
      default:
        if (error?.config?.onFail) {
          const parsedError = {
            message: error?.message,
            status: error?.response.status,
            response: error?.response.data,
            request: error?.config.data
          };
          error.config.onFail({ error: parsedError });
        }
        return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export const callApi = async ({ method = "GET", url, params, onFail }) => {
  const {
    contentType,
    responseType,
    orderBy,
    flatOrderBy,
    hasSeparatedOrderByParams,
    orderDirection,
    isExternal,
    ...paramsNoApiVersion
  } = params || {};

  const orderParams = getOrderParams(
    orderBy,
    orderDirection,
    flatOrderBy,
    hasSeparatedOrderByParams
  );

  const queryString = paramsNoApiVersion
    ? qsStringify({ ...paramsNoApiVersion, ...orderParams })
    : {};

  let apiUrl = `${process.env.REACT_APP_API_URL}${url}`;

  if (method === "GET") {
    apiUrl = queryString ? `${apiUrl}?${queryString}` : apiUrl;
  }

  let body = JSON.stringify(
    method === "GET"
      ? paramsNoApiVersion
      : emptyStringSanitizer({ orderBy, ...paramsNoApiVersion }) // adding orderBy as it's dropped in destrcuting
  );
  axiosApiInstance.defaults.onFail = onFail;
  return axiosApiInstance({ method, url: apiUrl, data: body });
};

export const getSingleItem = async (routeItemId, apiEndpoint) => {
  const response = await callApi({
    url: `/${apiEndpoint}/${routeItemId}`,
    method: "GET"
  });
  return response.data;
};

export const getListItems = async (filter, apiEndpoint, getFullResponse) => {
  const response = await callApi({
    url: `/${apiEndpoint}`,
    params: {
      ...filter
    }
  });
  return {
    items: response.data["hydra:member"],
    totalItems: response.data["hydra:totalItems"],
    fullResponse: getFullResponse ? response.data : null
  };
};

export const createItem = async ({ data, onFail, queryKey, apiEndpoint }) => {
  return await callApi({
    url: `/${apiEndpoint || queryKey}`,
    method: "POST",
    params: { ...data },
    onFail
  });
};

export const editItem = async ({ data, onFail, queryKey, apiEndpoint }) => {
  return await callApi({
    url: `/${apiEndpoint || queryKey}/${data.id}`,
    method: "PUT",
    params: { ...data },
    onFail
  });
};

export const deleteItem = async ({
  confirmId: id,
  onFail,
  queryKey,
  apiEndpoint
}) => {
  const response = await callApi({
    url: `/${apiEndpoint || queryKey}/${id}`,
    method: "DELETE",
    onFail
  });
  return response;
};

export const buildDeleteItem =
  ({ apiEndpoint, queryKey }) =>
  async ({ confirmId: id, onFail }) => {
    return await callApi({
      url: `/${apiEndpoint || queryKey}/${id}`,
      method: "DELETE",
      onFail
    });
  };

export const exportCSV = (params, url, fileNameParams) => async () => {
  const response = await callApi({
    method: "GET",
    url,
    params
  });
  doTriggerDownload(response.data, fileNameParams);
  return response.data;
};

let refreshPromise = null;
const clearPromise = () => (refreshPromise = null);
export const refreshAccessToken = ({ extendedSession = false } = {}) => {
  if (!refreshPromise) {
    refreshPromise = axios
      .get("/login/refresh_access_token.php", {
        params: extendedSession ? { extendedSession } : {}
      })
      .finally(clearPromise);
  }
  return refreshPromise;
};

export const updateAxiosToken = () => {
  const token = getToken();
  axiosApiInstance.defaults.headers.common["Authorization"] = "Bearer " + token;
};

const loggingDeduplication = new Map();
const DEDUP_WINDOW = 30000;
// prevents multiple logs of the same error (mostly common in lists)
export const logErrorSafely = ({ url, method, params }) => {
  const errorKey = `${params.message}:${params.level}`;
  const now = Date.now();
  const lastTime = loggingDeduplication.get(errorKey);

  if (lastTime && now - lastTime < DEDUP_WINDOW) {
    console.warn("Duplicate error log suppressed:", errorKey);
    return;
  }

  loggingDeduplication.set(errorKey, now);
  return callApi({ url, method, params });
};
